import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CaretLeft, Check, WarningCircle } from "@phosphor-icons/react";
import { BrandLockup } from "@/components/Branding";
import { AiHowDialog } from "@/components/AiConsent";
import { PhotoSlot } from "@/components/ebook/PhotoSlot";
import { CropDialog } from "@/components/ebook/CropDialog";
import { MemorySelector } from "@/components/ebook/MemorySelector";
import { EbookPreview } from "@/components/ebook/EbookPreview";
import { PhotoPrivacyPanel } from "@/components/ebook/PhotoPrivacyPanel";
import { StudioActions } from "@/components/ebook/StudioActions";
import { checkPrivateSupport, processPhoto, buildEbookPdf, MAX_PHOTOS } from "@/lib/ebookLocal";

const DRAFT_KEY = "ebook_draft";
const MAX_FILE_MB = 25;

export default function EbookStudio() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const kid = params.get("keepsake");
  const [data, setData] = useState(null);
  const [keepsake, setKeepsake] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState("");
  const [dedication, setDedication] = useState("");
  const [photos, setPhotos] = useState(Array(MAX_PHOTOS).fill(null));
  const [busyIdx, setBusyIdx] = useState(null);
  const [cropIdx, setCropIdx] = useState(null);
  const [supported] = useState(checkPrivateSupport);
  const [skipPhotos, setSkipPhotos] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [writing, setWriting] = useState(false);
  const [building, setBuilding] = useState(false);
  const srcs = useRef([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/keepsakes/ebook/preview");
        setData(r.data);
        const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
        const ids = r.data.chapters.flatMap((c) => c.memories.map((m) => m.id));
        setSelected(new Set(draft?.ids ? draft.ids.filter((id) => ids.includes(id)) : ids));
        setTitle(draft?.title || r.data.suggested_title);
        setDedication(draft?.dedication || "");
      } catch {
        toast.error("We couldn't prepare your Ebook studio.");
      }
    })();
  }, []);

  useEffect(() => {
    if (!kid) { setKeepsake(null); return; }
    api.get(`/keepsakes/${kid}`).then((r) => setKeepsake(r.data)).catch(() => toast.error("We couldn't open that Ebook."));
  }, [kid]);

  useEffect(() => {
    if (data && !keepsake) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ids: [...selected], title, dedication }));
    }
  }, [data, keepsake, selected, title, dedication]);

  const hasPhotos = photos.some(Boolean);
  useEffect(() => {
    if (!hasPhotos) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [hasPhotos]);

  useEffect(() => () => srcs.current.forEach((s) => s && URL.revokeObjectURL(s)), []);

  const memoryInfo = useMemo(() => {
    const out = {};
    (data?.chapters || []).forEach((c) => c.memories.forEach((m) => { out[m.id] = { title: m.title, season: c.season }; }));
    return out;
  }, [data]);

  const bookIds = useMemo(() => {
    if (keepsake?.memory_ids?.length) return new Set(keepsake.memory_ids);
    if (keepsake) return new Set(Object.keys(memoryInfo));
    return selected;
  }, [keepsake, selected, memoryInfo]);

  const bookChapters = useMemo(() => (data?.chapters || [])
    .map((c) => ({ ...c, memories: c.memories.filter((m) => bookIds.has(m.id)) }))
    .filter((c) => c.memories.length), [data, bookIds]);

  const sections = useMemo(() => (keepsake?.sections || []).map((s, i) => ({
    ...s, season: s.season || bookChapters[i]?.season || null,
  })), [keepsake, bookChapters]);

  const photoTargetsFor = useCallback((season) => {
    const ch = bookChapters.find((c) => c.season === season);
    return [`season:${season}`, ...(ch ? ch.memories.map((m) => `memory:${m.id}`) : [])];
  }, [bookChapters]);

  const contents = keepsake
    ? [...(keepsake.intro ? [{ key: "intro", label: "Prologue" }] : []),
       ...sections.map((s, i) => ({ key: `sec-${i}`, label: s.heading, photoTargets: s.season ? photoTargetsFor(s.season) : [] }))]
    : [...bookChapters.map((c) => ({ key: `season-${c.season}`, label: c.season, photoTargets: photoTargetsFor(c.season) })),
       { key: "looking-back", label: "Looking Back" }];
  const sectionText = keepsake
    ? Object.fromEntries([["intro", keepsake.intro], ...sections.map((s, i) => [`sec-${i}`, s.body])])
    : null;

  const targets = [
    { group: "Book cover", items: [{ value: "cover", label: "Front cover" }] },
    { group: "Life Seasons", items: bookChapters.map((c) => ({ value: `season:${c.season}`, label: c.season })) },
    { group: "Individual memories", items: bookChapters.flatMap((c) => c.memories.map((m) => ({ value: `memory:${m.id}`, label: m.title }))) },
  ];

  const setPhoto = (i, val) => setPhotos((prev) => prev.map((p, idx) => (idx === i ? val : p)));

  const onFile = async (i, file) => {
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`Please choose a photo under ${MAX_FILE_MB} MB.`); return; }
    setBusyIdx(i);
    const src = URL.createObjectURL(file);
    try {
      const out = await processPhoto(src, 0, null);
      if (srcs.current[i]) URL.revokeObjectURL(srcs.current[i]);
      srcs.current[i] = src;
      const usedCover = photos.some((p, idx) => idx !== i && p?.target === "cover");
      setPhoto(i, { src, name: file.name, rotation: 0, ...out, target: photos[i]?.target || (usedCover ? "" : "cover") });
    } catch {
      URL.revokeObjectURL(src);
      toast.error("This photo couldn't be opened on this device. Please try a JPG or PNG.");
    } finally {
      setBusyIdx(null);
    }
  };

  const reprocess = async (i, rotation, crop) => {
    const p = photos[i];
    setBusyIdx(i);
    try {
      const out = await processPhoto(p.src, rotation, crop);
      setPhoto(i, { ...p, rotation, ...out });
    } catch {
      toast.error("We couldn't update that photo.");
    } finally {
      setBusyIdx(null);
    }
  };

  const removePhoto = (i) => {
    if (srcs.current[i]) URL.revokeObjectURL(srcs.current[i]);
    srcs.current[i] = null;
    setPhoto(i, null);
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setMany = (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });

  const leave = (to) => {
    if (hasPhotos && !window.confirm("Your photos are only held on this page and will be cleared. Continue?")) return;
    navigate(to);
  };

  const write = async () => {
    setWriting(true);
    try {
      const r = await api.post("/keepsakes/generate", {
        type: "life_story_ebook", consent: true, title: title.trim(),
        dedication: dedication.trim() || null, memory_ids: [...selected],
      });
      localStorage.removeItem(DRAFT_KEY);
      setKeepsake(r.data);
      setParams({ keepsake: r.data.id }, { replace: true });
      toast.success("Your book is written. Add photos if you like, then download it.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" && d.startsWith("not_enough_memories:")
        ? d.split(":").slice(3).join(":")
        : formatApiErrorDetail(d) || "We couldn't write your book. Please try again.");
    } finally {
      setWriting(false);
    }
  };

  const download = () => {
    const placed = photos.filter((p) => p && p.target);
    if (photos.filter(Boolean).length > placed.length) toast.message("Photos without a placement were left out.");
    setBuilding(true);
    setTimeout(() => {
      try {
        buildEbookPdf({ keepsake, author: data?.author, photos: placed, memoryInfo });
        toast.success("Your Ebook was created on this device and downloaded.");
      } catch {
        toast.error("We couldn't create the PDF on this device.");
      } finally {
        setBuilding(false);
      }
    }, 50);
  };

  if (!data) return <p className="p-10 text-[#8A7F76]">Preparing your Ebook studio…</p>;

  const photosOn = supported && !skipPhotos;
  const editable = !keepsake;

  return (
    <div className="min-h-screen bg-[#FBF4E8] text-[#2C2A28]" data-testid="ebook-studio">
      <header className="border-b border-[#4A4540]/10 bg-[#FBF4E8]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <button data-testid="ebook-studio-back" onClick={() => leave("/timeline?view=keepsakes")} className="flex items-center gap-2 rounded-full border border-[#E07A5A]/40 bg-[#F6E2D8] px-4 py-2 text-sm font-semibold text-[#A84A32] shadow-sm transition-colors hover:bg-[#F0D2C4]">
            <CaretLeft size={16} weight="bold" /> Back to Keepsakes
          </button>
          <BrandLockup />
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A84A32]">My Life Story Ebook · Studio</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">{keepsake ? "Add your photos & download" : "Shape your book"}</h1>
        <p className="mt-3 max-w-2xl text-[#5c554e]">
          Your memories stay as text in your account. Photos are only added on this device, at the moment you
          create your Ebook, and go straight into your downloadable PDF.
        </p>
        {supported ? (
          <p data-testid="compat-check" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#EAF2EC] px-3 py-1 text-xs font-medium text-[#2E6B4F]">
            <Check size={13} weight="bold" /> This device can create photo Ebooks privately
          </p>
        ) : (
          <div data-testid="compat-unsupported" className="mt-3 max-w-2xl rounded-xl border border-[#E0A458]/50 bg-[#FBF0DD] p-3 text-sm text-[#8A5A2B]">
            <WarningCircle size={16} weight="fill" className="mr-1 inline align-text-bottom" />
            This device or browser can't add photos privately, so photos are switched off here. You can continue
            without photos, or open this page on another device. We never upload photos as a workaround.
          </div>
        )}

        <div className="mt-6"><PhotoPrivacyPanel /></div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-8">
            {editable && (
              <section>
                <h3 className="font-serif text-2xl">1. Choose your memories</h3>
                <p className="mb-3 mt-1 text-sm text-[#8A7F76]">Only the memories you tick are used — nothing is invented.</p>
                <MemorySelector chapters={data.chapters} selected={selected} onToggle={toggle} onSetMany={setMany} />
              </section>
            )}

            <section className="space-y-3">
              <h3 className="font-serif text-2xl">{editable ? "2. Title & dedication" : "Your book"}</h3>
              <div>
                <label className="text-sm font-medium">Book title</label>
                <Input data-testid="ebook-title-input" disabled={!editable} value={keepsake ? keepsake.title : title} onChange={(e) => setTitle(e.target.value)} className="mt-1 border-[#4A4540]/20 bg-white" />
              </div>
              <div>
                <label className="text-sm font-medium">Dedication <span className="text-[#8A7F76]">(optional)</span></label>
                <textarea data-testid="ebook-dedication-input" disabled={!editable} value={keepsake ? keepsake.dedication || "" : dedication} onChange={(e) => setDedication(e.target.value)} rows={2} placeholder="For my children, with all my love…" className="mt-1 w-full rounded-lg border border-[#4A4540]/20 bg-white p-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#E07A5A] disabled:opacity-70" />
              </div>
            </section>

            <section>
              <h3 className="font-serif text-2xl">{editable ? "3. " : ""}Choose up to 3 photos <span className="text-base text-[#8A7F76]">(optional)</span></h3>
              {photosOn ? (
                <>
                  <p className="mt-1 text-sm text-[#8A7F76]">{photos.filter(Boolean).length} of 3 selected · place each on the cover, a life season or a memory.</p>
                  <div className="mt-4 grid gap-5 sm:grid-cols-2">
                    {photos.map((p, i) => (
                      <PhotoSlot key={i} index={i} photo={p} targets={targets} busy={busyIdx === i} onFile={onFile}
                        onTarget={(idx, v) => setPhoto(idx, { ...photos[idx], target: v })}
                        onCrop={setCropIdx} onRotate={(idx) => reprocess(idx, (photos[idx].rotation + 90) % 360, null)}
                        onRemove={removePhoto} />
                    ))}
                  </div>
                  <p data-testid="refresh-note" className="mt-3 text-xs text-[#8A7F76]">If you close or refresh this page, your selected photos are cleared (they're only held on this device).</p>
                  {supported && (
                    <button type="button" data-testid="skip-photos-btn" onClick={() => { photos.forEach((p, i) => p && removePhoto(i)); setSkipPhotos(true); }} className="mt-2 py-2.5 text-sm text-[#6B635B] underline">Continue without photos</button>
                  )}
                </>
              ) : (
                <p data-testid="photos-off-note" className="mt-1 text-sm text-[#8A7F76]">
                  Continuing without photos.{" "}
                  {supported && <button type="button" data-testid="enable-photos-btn" onClick={() => setSkipPhotos(false)} className="underline">Add photos after all</button>}
                </p>
              )}
            </section>
          </div>

          <aside>
            <div className="sticky top-6">
              <EbookPreview title={keepsake ? keepsake.title : title} author={data.author} dedication={keepsake ? keepsake.dedication : dedication} contents={contents} photos={photos.filter((p) => p && p.target)} sectionText={sectionText} />
              <StudioActions
                owned={data.owned} keepsake={keepsake} enough={selected.size >= data.min} min={data.min}
                selectedCount={selected.size} hasPhotos={hasPhotos} aiConsent={aiConsent} setAiConsent={setAiConsent}
                onHow={() => setHowOpen(true)} photoConsent={photoConsent} setPhotoConsent={setPhotoConsent}
                writing={writing} building={building} onWrite={write} onDownload={download}
                onBuy={() => leave("/upgrade")}
                onRead={() => leave(`/keepsake/${keepsake.id}`)}
              />
            </div>
          </aside>
        </div>
      </main>

      <CropDialog photo={cropIdx !== null ? photos[cropIdx] : null} onClose={() => setCropIdx(null)}
        onApply={(area) => { const i = cropIdx; setCropIdx(null); reprocess(i, photos[i].rotation, area); }} />
      <AiHowDialog open={howOpen} onOpenChange={setHowOpen} />
    </div>
  );
}
